const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!customElements.get('ugc-proof-carousel')) {
  customElements.define(
    'ugc-proof-carousel',
    class UgcProofCarousel extends HTMLElement {
      constructor() {
        super();
        this.frame = null;
        this.timer = null;
        this.resizeObserver = null;
        this.intersectionObserver = null;
        this.isInView = false;
        this.isHovered = false;
        this.hasFocus = false;
        this.isUserPaused = false;
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onIntersect = this.onIntersect.bind(this);
        this.advance = this.advance.bind(this);
        this.update = this.update.bind(this);
      }

      connectedCallback() {
        if (!this.track || !this.progressBar) return;

        this.addEventListener('click', this.onClick);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        // Image load does not bubble, so listen in the capture phase: scrollWidth grows as slides decode.
        this.track.addEventListener('load', this.update, true);

        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);

        for (const slide of this.track.children) {
          this.resizeObserver.observe(slide);
        }

        if (this.hasAutoplay) this.setUpAutoplay();

        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.track?.removeEventListener('scroll', this.onScroll);
        this.track?.removeEventListener('load', this.update, true);
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.stopTimer();
      }

      /** @returns {HTMLElement | null} */
      get track() {
        return this.querySelector('[data-carousel-track]');
      }

      /** @returns {HTMLElement | null} */
      get progressBar() {
        return this.querySelector('[data-carousel-progress]');
      }

      /** @returns {HTMLButtonElement | null} */
      get toggleButton() {
        return this.querySelector('[data-carousel-toggle]');
      }

      /** @returns {boolean} */
      get hasAutoplay() {
        return this.hasAttribute('data-autoplay');
      }

      /**
       * Delay between automatic moves, clamped to the range the section setting allows.
       * @returns {number}
       */
      get interval() {
        const value = parseInt(this.dataset.interval, 10);
        if (Number.isNaN(value)) return 5000;

        return Math.min(Math.max(value, 3000), 10000);
      }

      /**
       * Width of one slide plus the gap after it, so each move advances exactly one pair.
       * @returns {number}
       */
      get stepSize() {
        const slide = this.track.firstElementChild;
        if (!slide) return this.track.clientWidth;

        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      }

      /** @returns {number} */
      get maxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      setUpAutoplay() {
        // Reduced motion: start paused; the visible play button still lets the shopper opt in.
        this.isUserPaused = prefersReducedMotion();

        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(this.onIntersect, { threshold: 0.25 });
        this.intersectionObserver.observe(this);

        this.updateToggle();
      }

      /** @param {MouseEvent} event */
      onClick(event) {
        if (event.target.closest('[data-carousel-toggle]')) {
          this.isUserPaused = !this.isUserPaused;
          this.updateToggle();
          this.refreshTimer();
          return;
        }

        const button = event.target.closest('[data-carousel-direction]');
        if (!button || !this.contains(button)) return;
        if (button.getAttribute('aria-disabled') === 'true') return;

        const direction = button.dataset.carouselDirection === 'next' ? 1 : -1;
        this.track.scrollBy({ left: direction * this.stepSize });
        this.refreshTimer();
      }

      /** @param {PointerEvent} event */
      onPointerEnter(event) {
        if (event.pointerType !== 'mouse') return;

        this.isHovered = true;
        this.refreshTimer();
      }

      onPointerLeave() {
        this.isHovered = false;
        this.refreshTimer();
      }

      onFocusIn() {
        this.hasFocus = true;
        this.refreshTimer();
      }

      /** @param {FocusEvent} event */
      onFocusOut(event) {
        if (this.contains(event.relatedTarget)) return;

        this.hasFocus = false;
        this.refreshTimer();
      }

      onVisibilityChange() {
        this.refreshTimer();
      }

      /** @param {IntersectionObserverEntry[]} entries */
      onIntersect(entries) {
        for (const entry of entries) {
          this.isInView = entry.isIntersecting;
        }

        this.refreshTimer();
      }

      onScroll() {
        if (this.frame) return;

        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      /**
       * Moves one pair forward, returning to the first pair after the last.
       */
      advance() {
        if (this.track.scrollLeft >= this.maxScroll - 1) {
          this.track.scrollTo({ left: 0 });
          return;
        }

        this.track.scrollBy({ left: this.stepSize });
      }

      refreshTimer() {
        this.stopTimer();

        const shouldRun = this.hasAutoplay && this.isInView && !this.isUserPaused && !this.isHovered && !this.hasFocus && !document.hidden;
        if (!shouldRun || this.maxScroll < 1) return;

        this.timer = window.setInterval(this.advance, this.interval);
      }

      stopTimer() {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      updateToggle() {
        const button = this.toggleButton;
        if (!button) return;

        this.toggleAttribute('data-paused', this.isUserPaused);
        button.setAttribute('aria-label', this.isUserPaused ? button.dataset.labelPlay : button.dataset.labelPause);
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const maxScroll = this.maxScroll;
        const visibleShare = scrollWidth > 0 ? Math.min(1, (scrollLeft + clientWidth) / scrollWidth) : 1;

        this.progressBar.style.setProperty('--ugc-proof-progress', visibleShare.toFixed(3));

        // aria-disabled rather than disabled: a disabled button drops keyboard focus at the end of the track.
        for (const button of this.querySelectorAll('[data-carousel-direction]')) {
          const isNext = button.dataset.carouselDirection === 'next';
          const atEnd = isNext ? scrollLeft >= maxScroll - 1 : scrollLeft <= 1;
          button.setAttribute('aria-disabled', String(atEnd));
        }
      }
    },
  );
}

if (!customElements.get('ugc-proof-video')) {
  customElements.define(
    'ugc-proof-video',
    class UgcProofVideo extends HTMLElement {
      constructor() {
        super();
        this.intersectionObserver = null;
        this.isAutoPausing = false;
        this.isUserPaused = false;
        this.onPlayClick = this.onPlayClick.bind(this);
        this.onIntersect = this.onIntersect.bind(this);
        this.onVideoPause = this.onVideoPause.bind(this);
        this.onVideoPlay = this.onVideoPlay.bind(this);
      }

      connectedCallback() {
        this.playButton?.addEventListener('click', this.onPlayClick);

        if (this.hasAttribute('data-autoplay') && this.video && !prefersReducedMotion()) this.setUpAutoplay();
      }

      disconnectedCallback() {
        this.playButton?.removeEventListener('click', this.onPlayClick);
        this.video?.removeEventListener('pause', this.onVideoPause);
        this.video?.removeEventListener('play', this.onVideoPlay);
        this.intersectionObserver?.disconnect();
      }

      /** @returns {HTMLButtonElement | null} */
      get playButton() {
        return this.querySelector('[data-video-play]');
      }

      /** @returns {HTMLVideoElement | null} */
      get video() {
        return this.querySelector('video');
      }

      /**
       * Plays muted while at least half the video is on screen and pauses when it leaves.
       * Native controls stay visible so the shopper can always pause it.
       */
      setUpAutoplay() {
        const { video, playButton } = this;

        video.controls = true;
        if (playButton) playButton.hidden = true;

        video.addEventListener('pause', this.onVideoPause);
        video.addEventListener('play', this.onVideoPlay);

        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(this.onIntersect, { threshold: 0.5 });
        this.intersectionObserver.observe(this);
      }

      /** @param {IntersectionObserverEntry[]} entries */
      async onIntersect(entries) {
        const { video } = this;

        for (const entry of entries) {
          if (!entry.isIntersecting) {
            // The pause event fires asynchronously, so the flag is cleared by onVideoPause, not here.
            if (!video.paused) {
              this.isAutoPausing = true;
              video.pause();
            }
            continue;
          }

          if (this.isUserPaused) continue;

          try {
            await video.play();
          } catch (error) {
            console.error(error);
          }
        }
      }

      /**
       * A pause the shopper made on screen is respected: scrolling back does not restart it.
       */
      onVideoPause() {
        if (this.isAutoPausing) {
          this.isAutoPausing = false;
          return;
        }

        this.isUserPaused = true;
      }

      onVideoPlay() {
        this.isUserPaused = false;
      }

      /**
       * The video stays unloaded (preload="none") until asked for, so it costs nothing on scroll.
       * @returns {Promise<void>}
       */
      async onPlayClick() {
        const { video, playButton } = this;
        if (!video) return;

        video.controls = true;
        playButton.hidden = true;
        // The play button had focus; hand it to the video so keyboard users are not dropped to <body>.
        video.focus();

        try {
          await video.play();
        } catch (error) {
          console.error(error);
        }
      }
    },
  );
}
